<script setup lang="ts">
import type { RouteLocationRaw } from 'vue-router';

interface Props {
  disabled?: boolean;
  ariaLabel?: string;
  /**
   * When set, renders a crawlable `<a>` (NuxtLink) instead of a `<button>`, so the click
   * target is a real link in the SSR HTML and navigates natively. Leave unset for the
   * default button behaviour driven by the `click` event.
   */
  to?: string | RouteLocationRaw;
}

const props = withDefaults(defineProps<Props>(), {
  disabled: false,
  ariaLabel: undefined,
  to: undefined,
});

const emit = defineEmits<{ click: [e: MouseEvent] }>();

const nuxtLink = resolveComponent('NuxtLink');

const onClick = (event: MouseEvent) => {
  if (props.disabled) {
    return;
  }

  emit('click', event);
};

const clickableClasses = computed(() =>
  cva('', {
    variants: {
      disabled: {
        true: 'cursor-not-allowed',
        false: 'cursor-pointer',
      },
    },
  })({ disabled: props.disabled }),
);
</script>

<template>
  <component
    :is="to && !disabled ? nuxtLink : 'button'"
    :class="clickableClasses"
    :to="to && !disabled ? to : undefined"
    :disabled="to && !disabled ? undefined : disabled"
    :aria-disabled="disabled"
    :aria-label="ariaLabel"
    @click="onClick"
  >
    <slot />
  </component>
</template>
